import { Users, Award, Globe, Target } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import aboutTeam from "@/assets/about-team.jpg";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const team = [
    { name: "Aida Djigo Wane", role: "Directrice Générale", description: "Économiste financier sénior, +25 ans d'expérience en ingénierie financière et économique, ancienne DGA de l'APIX", specialties: ["Finance publique", "Econométrie", "Modélisation"] },
    { name: "Arfang Latif Bodian", role: "Directeur Adjoint", description: "Expert en modélisation financière et économétrie", specialties: ["Modélisateur", "Econométre", "Statisticien"] },
    { name: "Elisa Diédhiou", role: "Economiste", description: "Spécialiste en économie et modélisation financière", specialties: ["Economie et Finance"] },
    { name: "Alioune Gueye", role: "Informaticien", description: "Spécialiste en data engineering et développement d'application", specialties: ["Data Engineering", "ETL", "Dashboard", "Développement"] },
    { name: "Mame Fatou Gueye", role: "Economiste", description: "Spécialiste en économétrie et modélisation financière", specialties: ["Economie et Finance"] },
    { name: "Justine Diarra", role: "Administratrice", description: "Gestion administrative et coordination des projets", specialties: ["Administration"] }
  ];

  const values = [
    { icon: Target, title: "Excellence", description: "Les meilleures méthodologies et outils d'analyse." },
    { icon: Users, title: "Collaboration", description: "Étroite collaboration avec nos clients." },
    { icon: Award, title: "Expertise", description: "Expertise académique et expérience terrain." },
    { icon: Globe, title: "Innovation", description: "Technologies et méthodes de pointe." },
  ];

  return (
    <section id="about" className="py-24 bg-muted/30" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">Qui sommes-nous</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">À Propos de Nous</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Fondée en 2019, notre société accompagne les entreprises et institutions dans leurs décisions stratégiques les plus importantes.
          </p>
        </motion.div>

        {/* Story section with image */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20 items-center">
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">Notre Histoire</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Créée par une économiste et financière, Madame la Directrice Générale Aida Djigo Wane, notre société s'est rapidement 
                imposée comme une référence en matière d'études économiques et financières et en transformation digitale.
              </p>
              <p>
                Nous avons accompagné plus de 15 projets, des PME aux grandes corporations, 
                en passant par les institutions publiques, dans leurs enjeux de croissance, 
                de transformation et d'optimisation.
              </p>
              <p>
                Notre approche combine rigueur méthodologique, innovation technologique 
                et compréhension fine des enjeux sectoriels.
              </p>
            </div>
          </motion.div>
          
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-[var(--shadow-premium)]">
              <img src={aboutTeam} alt="Notre équipe" className="w-full h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy))/0.4] to-transparent" />
            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-card rounded-xl p-3 sm:p-4 shadow-lg border border-border">
              <div className="text-3xl font-bold text-[hsl(var(--brand-red))]">6+</div>
              <div className="text-xs text-muted-foreground font-medium">Années d'expérience</div>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">Nos Valeurs</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                className="text-center p-8 rounded-2xl bg-card border border-border hover:border-[hsl(var(--brand-red))/0.3] hover:shadow-[var(--shadow-card)] transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[hsl(var(--brand-red))/0.1] to-[hsl(var(--brand-navy))/0.1] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <value.icon className="h-7 w-7 text-[hsl(var(--brand-red))]" />
                </div>
                <h4 className="text-lg font-bold text-foreground mb-2">{value.title}</h4>
                <p className="text-muted-foreground text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div>
          <div className="bg-gradient-to-r from-[hsl(var(--brand-red))/0.1] to-[hsl(var(--brand-navy))/0.1] rounded-2xl p-8 mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center">Notre Équipe</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 + index * 0.1, duration: 0.5 }}
                className="group relative h-full overflow-hidden rounded-2xl bg-card border border-border hover:border-[hsl(var(--brand-red))/0.3] hover:shadow-[var(--shadow-premium)] transition-all duration-500 p-6"
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <Avatar className="h-20 w-20 ring-4 ring-muted group-hover:ring-[hsl(var(--brand-red))/0.2] transition-all">
                    <AvatarFallback className="bg-gradient-to-br from-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))] text-white text-xl font-bold">
                      {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="text-lg font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors">{member.name}</h4>
                    <p className="text-sm font-semibold text-[hsl(var(--brand-navy))]">{member.role}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{member.description}</p>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {member.specialties.map((s, i) => (
                      <span key={i} className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground border border-border">{s}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
